/**
 * **同梱の地形ドット絵** (#605)。基本地形と追加バイオームの既定の見た目。
 *
 * 絵レコード (エディタで描いたもの) が無い地形に使う。管理者 PDS に何も書かなくても
 * 全環境がこの見た目になり、エディタで描き直せばレコードが勝つ (既定に過ぎない)。
 * 草色 (#3f9d3f) は城・ダンジョン入口のプリセット (part-presets) と揃えてある —
 * 隣接タイルの草が別の緑だと、タイルの継ぎ目が格子に見えてしまう。
 *
 * **`tile-art.ts` から import される前提の純データモジュール** (型だけ import する。
 * 実装を import すると循環になる)。
 *
 * **palette[0] は透明 ('') に予約する** — エディタは索引 0 を消しゴム (透明) 固定で
 * 扱うので、ここに実色を置くと「透明を塗ったのに緑が出る」食い違いになる。
 */
import type { TileArtRecord } from './tile-art.js';

export const DEFAULT_TERRAIN_ARTS: Record<string, TileArtRecord> = {
  // snowfield: original 16×16 terrain artwork.
  snowfield: {
    size: 16,
    palette: ["", "#e7f3fa", "#c4deed", "#ffffff", "#a9cbdc"],
    pixels: 'AQEBAQEBAQEBAQEBAQEBAQEBAQEBAwMDAQEBAQEBAQEBAQEDAwICAQEBAQEBAQEBAQEBAgICAQEBAQEBAQEDAQEBAQEBAQEBAQEBAQEDAwEBAQEBAQEBAQEBAQEBAgIBAwEBAQEBAQEBAQEBAQEBAwMDAQEBAQEBAQEBAQEBAQECAgEBAQEBAQEBAQMDAwEBAQEBAQEBAQEBAQMCAgEBAQEBAQEBAQEBAQECAgEBAQEBAQEBAwMDAwEBAQEBAQEBAQEBAwMCAgEBAQEBAQEBAQEBAQICAgQBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ==',
  },
  // snowMountain: original 16×16 terrain artwork.
  snowMountain: {
    size: 16,
    palette: ["", "#e7f3fa", "#c4deed", "#ffffff", "#476981", "#7093ae", "#a9cbdc"],
    pixels: 'AQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEDAQEBAQEBAQEBAQEBAQEDAwMBAQEBAQEBAQEBAQEDAwMDBgEBAQEBAQEBAQEDAwMDBgYGAQEBAQEBAQEDAwMDAwMGBgYBAQEBAQEDAwMDBgMDAwYGBgEBAQEBBAQDBgQGAwQGBgYEAQEBAQQFBQYEBQUEBgYGBAEBAQQEBQYFBAUGBQQGBgQEAQEEBQUGBQQEBQUEBAYGBAEEBAUFBgUEBAQFBAQGBgQEAQQEBAQEBAQEBAQEBAQEAQEBAgICAgICAgICAgICAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ==',
  },
  // desert: original 16×16 terrain artwork.
  desert: {
    size: 16,
    palette: ["", "#dfb85e", "#c59843", "#f5d887", "#eed08a"],
    pixels: 'AQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEDAwMDAQEBAQEBAQEBAQMDBAQEAwMDAQEBAQEBAwQEAQEBAQICAgEBAQEBAwQBAQEBAQEBAQICAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQMBAQEBAQEBAQEBAQMDAwMEAwMDAQEBAQEBAwMDBAQEAQICAgMDAwMDAwQEAQEBAQEBAQECAgICAgIBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQMDAwEBAQEBAQEBAQEBAQMEAgIBAQEBAQEEAQEBAQECAgEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ==',
  },

  // 草原 — 明るい草地にまばらな濃い葉
  plains: {
    size: 16,
    palette: ['', '#3f9d3f', '#358535', '#2c6e2c', '#55b855'],
    pixels:
      'AQEBAQEBAQIBAQEBAQEBAQEBBAEBAQEBAQEBAgEBAQEBAQEBAQEBAQEBAQEBAQQBAQIBAQEBAgIBAQEBAQEBAQEBAQEBAQEBAQEBAwEBAQEBAQEBBAEBAQEBAQEBAQIBAQEBAQEBAQEBAgEBAQEBAQIBAQEBAQEBAQEBAQEEAQEBAQEDAQEBAQEBAQEBAQEBAQEBAQEBAQEEAQEBAgEBAQEBAQEBAgEBAQEBAQEBAQEBBAEBAQEBAQEBAQEBAQIBAQEBAQEBAQMBAQEBAQEBAQEBAQIBAQEBAQEEAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQECAQEBAQEBAQMBAQ==',
  },
  // 林 — 草地に丸い木が点在
  grove: {
    size: 16,
    palette: ['', '#3f9d3f', '#358535', '#2c6e2c', '#1f5c2a', '#2e7d3a', '#49a34f', '#6b4226'],
    pixels:
      'AQEBAQEBAQEBAQEBAgEBAQEBAQUFAQEBAQEBAQEBAQEBAQUGBgUBAQEBAQEBAQEBAQUGBgYGBQEBAQECAQEBAQEFBgUGBgUBAQEBAQEBAQEBBAUFBgUEAQEBAQUFAQEBAQEEBQUEAQEBAQUGBgUBAQEBAQcHAQEBAQEFBgUFAQEBAgEHBwEBAQEBBAUFBAEBAQEBAQEBAQEBAQEHBwEBAQEBAQEBAQIBAQEBBwcBAgEBAQIBAQEBAQEBAQEBAQEBAQEBAQEBAQEDAQEBAQEBAQEBAQECAQEBAQEBAQECAQEBBgEBAQEBAQEBAQEBAQEBAQEBAQEBAQIBAQEBAQEBAQ==',
  },
  // 森 — 濃い樹冠のテクスチャ
  forest: {
    size: 16,
    palette: ['', '#1f5c2a', '#2e7d3a', '#49a34f', '#2c6e2c'],
    pixels:
      'AgMDAgECAgMDAgIBAgMDAgMDAwMCAwMDAwMDAgMDAwMCAwMCAgMDAgIDAwICAwMCAQICAQECAgEBAgIBAQICAQEEAQEBAQQBAQEEAQEEAQECAgECAgMDAgIBAgIDAwICAwMCAwMDAwMDAgMDAwMDAwIDAwMCAwMCAwMCAwMCAwIBAgMCAQICAQIDAgECAgEBAQEEAQEBBAQBAQQBAQQBAQIDAwIBAgIDAwICAQIDAwIDAwMDAgMDAwMDAwIDAwMDAgMDAgIDAwICAwMCAgMDAgECAgEBAgIBAQICAQECAgEBBAEBAQEEAQEBBAEBBAEBAgEBAgIBAQICAQECAgEBAg==',
  },
  // 池 — 草地に囲まれた小さな水面
  pond: {
    size: 16,
    palette: ['', '#3f9d3f', '#358535', '#2c6e2c', '#1e5fa8', '#2b74c4', '#7db8e8', '#b8d9f2'],
    pixels:
      'AQEBAQEBAgEBAQEBAQEBAQEBAQEDAwMDAwMBAQECAQEBAQMDBAQEBAQEAwMBAQEBAQMEBAUFBQUFBQQEAwEBAQEDBAUFBQYGBQUFBAMBAQECAwQFBgcGBQUFBQQDAQEBAQMEBQYGBQUFBQUEAwECAQEDBAUFBQUFBQYFBAMBAQEBAwQFBQUFBQYHBgQDAQEBAQMEBAUFBQUFBgUEAwEBAQEBAwQEBQUFBQUEBAMBAQEBAQEDAwQEBAQEAwMBAQEBAQECAQEDAwMDAwEBAQEBAQEBAQEBAQEBAQEBAgEBAQEBAQEBAgEBAQEBAQEBAQEBAQEBAQEBAQEBAgEBAQEBAQ==',
  },
  // 海 — 深い青に波頭
  water: {
    size: 16,
    palette: ['', '#1e5fa8', '#2b74c4', '#7db8e8', '#b8d9f2'],
    pixels:
      'AQECAgEBAQEBAQECAgEBAQECAwMCAQEBAQECAwMCAQEBAQICAQEBAQEBAQICAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQICAQEBAQEBAQEBAQEBAQIDBAIBAQEBAQEBAQEBAQEBAgIBAQEBAQEBAQEBAQEBAQEBAQEBAQECAgEBAgIBAQEBAQEBAQECAwMCAgMDAgEBAQEBAQEBAQICAQECAgEBAQEBAQEBAQEBAQEBAQEBAQEBAQICAQEBAQEBAQEBAQEBAQIDBAIBAQEBAQEBAQEBAQEBAgIBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQECAgEBAQEBAQECAgEBAQ==',
  },
  // 山 — 雪をかぶった岩峰
  mountain: {
    size: 16,
    palette: ['', '#3f9d3f', '#358535', '#3a3026', '#5c5044', '#8a7a64', '#b0a088', '#eef2f6', '#c9d4de'],
    pixels:
      'AQEBAQEBAQIBAQEBAQEBAQECAQEBAQMDAQEBAQEBAgEBAQEBAQMHBwMBAQEBAQEBAQEBAQMHBwgHAwEBAQEBAQEBAQEDBggHBwMBAQEBAQEBAQEDBgYFBQQHAwEBAQEBAQEBAwYFBQUFBAMBAQEBAQEBAwYGBQUFBQQEAwEDAwEBAQMGBQUFBQUFBAMDBQQDAQMGBgUFBQUFBQQEAwUEAwEDBgUFBQUFBQUFBAUFBAMDBgYFBQUFBQUFBQQEBQQDAwYFBQUFBQUFBQUFBAQEAwMDAwMDAwMDAwMDAwMDAwMCAQECAQEBAQEBAQIBAQECAQEBAQEBAgEBAQEBAQEBAQ==',
  },
  // 街 — 赤屋根の家並み
  town: {
    size: 16,
    palette: ['', '#3f9d3f', '#358535', '#2b2b38', '#c44536', '#96352a', '#e8dcc0', '#c9b995', '#20242e', '#c2a878', '#6b4226'],
    pixels:
      'AQEBAQEBAQEBAQEBAQIBAQECAQMDAwMDAwEBAQEBAQEBAQMEBAQEBAQDAQEBAQEBAQMEBAQEBAQEBAMBAQEBAQMEBAQEBAQEBAQEAwMDAwEDBQUFBQUFBQUFBQMEBAMBAQMGBgcGBgcGBgMEBAQEAwEDBggHBgYHCAYDBQUFBQMBAwYGBwYGBwYGAwYHBgMBAQMGBgcDAwcGBgMGCAYDAQEDBgYHAwoHBgYDBgYGAwEBAwMDAwMKAwMDAwMDAwMBAQEJCQEDCgMBCQkBAQEBAQIBAQkJCQkJCQkBAQECAQEBAQEBAQkJAQEBAQEBAQEBAQEBAgEBAQECAQEBAQEBAQ==',
  },
  // 橋 — 水をまたぐ木の橋 (横断)
  bridge: {
    size: 16,
    palette: ['', '#1e5fa8', '#2b74c4', '#7db8e8', '#4e2f16', '#7a4a26', '#a06a32', '#c08a4a'],
    pixels:
      'AQECAgEBAQEBAQECAgEBAQECAwMCAQEBAQECAwMCAQEBAQICAQEBAQEBAQICAQEBBAQEBAQEBAQEBAQEBAQEBAUFBQUFBQUFBQUFBQUFBQUGBgcGBgYHBgYGBwYGBgcGBgYGBgYGBgYGBgYGBgYGBgcGBgYHBgYGBwYGBgcGBgYGBgYGBgYGBgYGBgYGBgYGBgYHBgYGBwYGBgcGBgYHBgUGBgYFBgYGBQYGBgUGBgYFBQUFBQUFBQUFBQUFBQUFBAQEBAQEBAQEBAQEBAQEBAEBAgIBAQEBAQEBAgIBAQEBAgMDAgEBAQEBAgMDAgEBAQECAgEBAQEBAQEBAgIBAQ==',
  },
};
